import type { GameTheme } from "./types";

const graphics = "/assets/moormenners/graphics";
const sounds = "/assets/moormenners/sounds";
const bigChickenFrameSequence = [
  0, 1, 2, 3, 4, 5, 6, 7,
  8, 9, 10, 11, 12, 13, 14, 15,
  16, 17, 18, 18, 18, 18, 18, 18,
  16, 17, 18, 18, 18, 18, 18,
  8, 9, 10, 11, 12, 13, 14, 15,
  16, 17, 18, 18, 18, 18, 18, 18,
  16, 17, 18, 18, 18, 18, 18,
  8, 9, 10, 11, 12, 13, 14, 15,
  16, 17, 18, 18, 18, 18, 18, 18,
  16, 17, 18, 18, 18, 18, 18,
  7, 6, 5, 4, 3, 2, 1, 0
];

export const moormennersTheme: GameTheme = {
  id: "moormenners-range",
  version: "2026.06.20-death-variants",
  title: "Moormenners",
  subtitle: "90 seconds, 3200 pixels of trouble.",
  round: {
    durationMs: 90_000,
    viewportWidth: 800,
    viewportHeight: 600,
    worldWidth: 3200,
    worldHeight: 600,
    magazineSize: 8,
    reloadMs: 820,
    cameraSpeed: 560,
    maxActiveTargets: 10,
    spawnIntervalMs: [620, 1160],
    spawnGate: {
      checksPerSecond: 60,
      chance: 50,
      events: [
        "normal", "normal", "normal", "normal", "normal", "normal", "normal", "normal",
        "normal", "normal", "normal", "normal", "normal", "normal", "none", "normal",
        "normal", "normal", "normal", "normal", "normal", "normal", "normal", "normal",
        "normal", "normal", "normal", "normal", "normal", "none", "none", "none",
        "bigChicken"
      ],
      normalTargetIds: [
        "nearChicken", "nearChicken", "nearChicken", "nearChicken",
        "midChicken", "midChicken", "midChicken",
        "bonusFarChicken"
      ]
    },
    missPenalty: 0
  },
  ui: {
    fontFamily: "Arial Black, Impact, system-ui, sans-serif",
    colors: {
      page: "#11131a",
      panel: "#f5ead1",
      panelStrong: "#31251f",
      text: "#1f1d19",
      muted: "#756b5d",
      accent: "#c33427",
      accentAlt: "#286d63",
      danger: "#b3261e",
      success: "#236d3f",
      line: "#c8a56c"
    },
    copy: {
      start: "Start",
      leaderboard: "Highscores",
      submit: "Save",
      restart: "Again",
      resume: "Resume",
      namePlaceholder: "Name",
      gameOver: "Game over",
      paused: "Paused",
      reload: "Reload"
    },
    loadingUrl: `${graphics}/screen_loading.jpg`,
    titleCardUrl: `${graphics}/screen_title_card.jpg`,
    highscoreUrl: `${graphics}/screen_highscore.jpg`,
    ammoShellUrl: `${graphics}/ui_ammo_shell_initial.png`,
    ammoReloadUrl: `${graphics}/ui_ammo_shell_reload_frames.png`
  },
  crosshair: {
    key: "crosshair",
    url: `${graphics}/ui_crosshair_cursor_3frames.png`,
    frameWidth: 40,
    frameHeight: 40,
    frames: 3,
    frameRate: 8,
    repeat: -1
  },
  effects: {
    bulletHoleUrl: `${graphics}/effect_bullet_hole.png`,
    treeHoleUrl: `${graphics}/sprite_hole.png`,
    particle: {
      key: "particle",
      url: `${graphics}/effect_particle_6frames.png`,
      frameWidth: 4,
      frameHeight: 4,
      frames: 6,
      frameRate: 18,
      repeat: 0
    }
  },
  backgroundLayers: [
    {
      id: "sky",
      key: "background-sky",
      url: `${graphics}/background_sky_2000x600.png`,
      width: 2000,
      height: 600,
      depth: 0
    },
    {
      id: "mid",
      key: "background-mid",
      url: `${graphics}/background_mid_2200x600.png`,
      width: 2200,
      height: 600,
      depth: 2
    },
    {
      id: "lower",
      key: "background-lower",
      url: `${graphics}/background_lower_2450x600.png`,
      width: 2450,
      height: 600,
      depth: 4
    },
    {
      id: "overlay-lower",
      key: "background-overlay-lower",
      url: `${graphics}/background_overlay_lower_2800x600.png`,
      width: 2800,
      height: 600,
      depth: 70
    },
    {
      id: "foreground",
      key: "background-foreground",
      url: `${graphics}/background_foreground_overlay_3200x600.png`,
      width: 3200,
      height: 600,
      depth: 170
    }
  ],
  worldSprites: [
    {
      id: "signpost",
      key: "signpost",
      url: `${graphics}/sprite_sign.png`,
      x: 758,
      y: 121,
      depth: 146,
      frameWidth: 450,
      frameHeight: 479,
      frame: 1,
      hitFrame: 0,
      scale: 1,
      originX: 0,
      originY: 0
    },
    {
      id: "field-scarecrow",
      key: "field-scarecrow",
      url: `${graphics}/sprite_pumpkin.png`,
      x: 2052,
      y: 312,
      depth: 78,
      frameWidth: 104,
      frameHeight: 160,
      frames: 8,
      frameRate: 13,
      repeat: 0,
      frame: 0,
      hitFrame: 7,
      scale: 1,
      originX: 0,
      originY: 0,
      layerId: "overlay-lower"
    }
  ],
  sceneryHitZones: [
    {
      id: "signpost",
      x: 758,
      y: 121,
      width: 450,
      height: 479,
      depth: 148,
      sound: "shieldHit",
      decal: false,
      penalty: -25,
      hitBoxesByFrame: {
        0: [
          { offsetX: 219, offsetY: 43, width: 194, height: 56 }
        ],
        1: [
          { offsetX: 0, offsetY: 50, width: 205, height: 75 },
          { offsetX: 58, offsetY: 154, width: 150, height: 64 }
        ]
      }
    },
    { id: "field-scarecrow", x: 2052, y: 312, width: 104, height: 160, depth: 82, sound: "scarecrowHit", decal: false, points: 25, layerId: "overlay-lower" },
    { id: "left-tree", x: 431, y: 0, width: 145, height: 600, depth: 178, sound: "treeHit", decal: true, layerId: "foreground" },
    { id: "right-tree", x: 2176, y: 0, width: 75, height: 600, depth: 178, sound: "treeHit", decal: true, layerId: "foreground" }
  ],
  targets: [
    {
      id: "nearChicken",
      label: "Near chicken",
      behavior: "fly",
      points: 5,
      spawnWeight: 9,
      hitRadius: 54,
      speed: [100, 100],
      flight: {
        verticalVelocity: [-40, 40],
        turnIntervalMs: [1000, 2900],
        boundsY: [-100, 440]
      },
      yRange: [0, 256],
      lifetimeMs: [90_000, 90_000],
      scale: 1,
      depth: 92,
      layerId: "overlay-lower",
      asset: {
        key: "near-chicken-fly",
        url: `${graphics}/sprite_chicken_big_near_flying_13frames_1.png`,
        frameWidth: 140,
        frameHeight: 152,
        frames: 13,
        frameRate: 13,
        repeat: -1
      },
      assetVariants: [
        {
          asset: {
            key: "near-chicken-fly-2",
            url: `${graphics}/sprite_chicken_big_near_flying_13frames_2.png`,
            frameWidth: 140,
            frameHeight: 152,
            frames: 13,
            frameRate: 13,
            repeat: -1
          },
          deathAsset: {
            key: "near-chicken-death-2",
            url: `${graphics}/sprite_chicken_big_death_fall_8frames_2.png`,
            frameWidth: 140,
            frameHeight: 152,
            frames: 8,
            frameRate: 14,
            repeat: 0
          }
        },
        {
          asset: {
            key: "near-chicken-fly-3",
            url: `${graphics}/sprite_chicken_big_near_flying_13frames_3.png`,
            frameWidth: 140,
            frameHeight: 152,
            frames: 13,
            frameRate: 13,
            repeat: -1
          },
          deathAsset: {
            key: "near-chicken-death-3",
            url: `${graphics}/sprite_chicken_big_death_fall_8frames_3.png`,
            frameWidth: 140,
            frameHeight: 152,
            frames: 8,
            frameRate: 14,
            repeat: 0
          }
        }
      ],
      deathAsset: {
        key: "near-chicken-death",
        url: `${graphics}/sprite_chicken_big_death_fall_8frames.png`,
        frameWidth: 140,
        frameHeight: 152,
        frames: 8,
        frameRate: 14,
        repeat: 0
      },
      hitSound: "chickenHit1"
    },
    {
      id: "midChicken",
      label: "Mid chicken",
      behavior: "fly",
      points: 10,
      spawnWeight: 10,
      hitRadius: 44,
      speed: [50, 50],
      flight: {
        verticalVelocity: [-30, 30],
        turnIntervalMs: [1000, 2900],
        boundsY: [-100, 440]
      },
      yRange: [0, 128],
      lifetimeMs: [35_000, 35_000],
      scale: 1,
      depth: 62,
      layerId: "overlay-lower",
      asset: {
        key: "mid-chicken-fly",
        url: `${graphics}/sprite_chicken_normal_flying_13frames_1.png`,
        frameWidth: 120,
        frameHeight: 120,
        frames: 13,
        frameRate: 13,
        repeat: -1
      },
      assetVariants: [
        {
          asset: {
            key: "mid-chicken-fly-2",
            url: `${graphics}/sprite_chicken_normal_flying_13frames_2.png`,
            frameWidth: 120,
            frameHeight: 120,
            frames: 13,
            frameRate: 13,
            repeat: -1
          },
          deathAsset: {
            key: "mid-chicken-death-2",
            url: `${graphics}/sprite_chicken_normal_death_fall_8frames_2.png`,
            frameWidth: 120,
            frameHeight: 120,
            frames: 8,
            frameRate: 14,
            repeat: 0
          }
        },
        {
          asset: {
            key: "mid-chicken-fly-3",
            url: `${graphics}/sprite_chicken_normal_flying_13frames_3.png`,
            frameWidth: 120,
            frameHeight: 120,
            frames: 13,
            frameRate: 13,
            repeat: -1
          },
          deathAsset: {
            key: "mid-chicken-death-3",
            url: `${graphics}/sprite_chicken_normal_death_fall_8frames_3.png`,
            frameWidth: 120,
            frameHeight: 120,
            frames: 8,
            frameRate: 14,
            repeat: 0
          }
        }
      ],
      deathAsset: {
        key: "mid-chicken-death",
        url: `${graphics}/sprite_chicken_normal_death_fall_8frames_1.png`,
        frameWidth: 120,
        frameHeight: 120,
        frames: 8,
        frameRate: 14,
        repeat: 0
      },
      hitSound: "chickenHit2"
    },
    {
      id: "farChicken",
      label: "Far chicken",
      behavior: "fly",
      points: 15,
      spawnWeight: 0,
      hitRadius: 24,
      speed: [25, 25],
      flight: {
        verticalVelocity: [-20, 20],
        turnIntervalMs: [1000, 2900],
        boundsY: [-100, 240]
      },
      yRange: [0, 128],
      lifetimeMs: [17_000, 17_000],
      scale: 1,
      depth: 28,
      layerId: "lower",
      asset: {
        key: "far-chicken-fly",
        url: `${graphics}/sprite_chicken_small_far_flying_13frames_1.png`,
        frameWidth: 44,
        frameHeight: 32,
        frames: 13,
        frameRate: 13,
        repeat: -1
      },
      assetVariants: [
        {
          asset: {
            key: "far-chicken-fly-2",
            url: `${graphics}/sprite_chicken_small_far_flying_13frames_2.png`,
            frameWidth: 44,
            frameHeight: 32,
            frames: 13,
            frameRate: 13,
            repeat: -1
          },
          deathAsset: {
            key: "far-chicken-death-2",
            url: `${graphics}/sprite_chicken_small_death_fall_8frames_2.png`,
            frameWidth: 44,
            frameHeight: 32,
            frames: 8,
            frameRate: 14,
            repeat: 0
          }
        },
        {
          asset: {
            key: "far-chicken-fly-3",
            url: `${graphics}/sprite_chicken_small_far_flying_13frames_3.png`,
            frameWidth: 44,
            frameHeight: 32,
            frames: 13,
            frameRate: 13,
            repeat: -1
          },
          deathAsset: {
            key: "far-chicken-death-3",
            url: `${graphics}/sprite_chicken_small_death_fall_8frames_3.png`,
            frameWidth: 44,
            frameHeight: 32,
            frames: 8,
            frameRate: 14,
            repeat: 0
          }
        }
      ],
      deathAsset: {
        key: "far-chicken-death",
        url: `${graphics}/sprite_chicken_small_death_fall_8frames_1.png`,
        frameWidth: 44,
        frameHeight: 32,
        frames: 8,
        frameRate: 14,
        repeat: 0
      },
      hitSound: "chickenHit2"
    },
    {
      id: "bonusFarChicken",
      label: "Fast far chicken",
      behavior: "fly",
      points: 25,
      spawnWeight: 0,
      hitRadius: 20,
      speed: [16, 16],
      flight: {
        verticalVelocity: [-14, 14],
        turnIntervalMs: [1000, 2900],
        boundsY: [-100, 240]
      },
      yRange: [0, 100],
      lifetimeMs: [11_000, 11_000],
      scale: 1,
      depth: 26,
      layerId: "mid",
      asset: {
        key: "bonus-far-chicken-fly",
        url: `${graphics}/sprite_chicken_small_far_flying_13frames_1.png`,
        frameWidth: 44,
        frameHeight: 32,
        frames: 13,
        frameRate: 13,
        repeat: -1
      },
      assetVariants: [
        {
          asset: {
            key: "bonus-far-chicken-fly-2",
            url: `${graphics}/sprite_chicken_small_far_flying_13frames_2.png`,
            frameWidth: 44,
            frameHeight: 32,
            frames: 13,
            frameRate: 13,
            repeat: -1
          },
          deathAsset: {
            key: "bonus-far-chicken-death-2",
            url: `${graphics}/sprite_chicken_small_death_fall_8frames_2.png`,
            frameWidth: 44,
            frameHeight: 32,
            frames: 8,
            frameRate: 14,
            repeat: 0
          }
        },
        {
          asset: {
            key: "bonus-far-chicken-fly-3",
            url: `${graphics}/sprite_chicken_small_far_flying_13frames_3.png`,
            frameWidth: 44,
            frameHeight: 32,
            frames: 13,
            frameRate: 13,
            repeat: -1
          },
          deathAsset: {
            key: "bonus-far-chicken-death-3",
            url: `${graphics}/sprite_chicken_small_death_fall_8frames_3.png`,
            frameWidth: 44,
            frameHeight: 32,
            frames: 8,
            frameRate: 14,
            repeat: 0
          }
        }
      ],
      deathAsset: {
        key: "bonus-far-chicken-death",
        url: `${graphics}/sprite_chicken_small_death_fall_8frames_1.png`,
        frameWidth: 44,
        frameHeight: 32,
        frames: 8,
        frameRate: 14,
        repeat: 0
      },
      hitSound: "chickenHit3"
    },
    {
      id: "bigChicken",
      label: "Special chicken",
      behavior: "popup",
      points: 25,
      spawnWeight: 0,
      hitRadius: 116,
      speed: [0, 0],
      yRange: [667, 667],
      scale: 1,
      depth: 150,
      originX: 0,
      originY: 1,
      lifetimeMs: [1500, 3000],
      popupMode: "sheet",
      worldXRange: [0, 2272],
      oncePerRound: true,
      frameSequence: bigChickenFrameSequence,
      frameSequenceRate: 24,
      asset: {
        key: "big-chicken",
        url: `${graphics}/sprite_special_big_chicken_19frames_clean.png`,
        frameWidth: 380,
        frameHeight: 380,
        frames: 19,
        frameRate: 8,
        repeat: 0
      },
      hitAsset: {
        key: "big-chicken-hit",
        url: `${graphics}/sprite_special_big_chicken_hit_6frames.png`,
        frameWidth: 380,
        frameHeight: 380,
        frames: 6,
        frameRate: 12,
        repeat: 0
      },
      spawnSound: "bigChickenPop",
      hitSound: "chickenHit1"
    },
    {
      id: "windmillChicken",
      label: "Windmill chicken",
      behavior: "fixed",
      points: 25,
      spawnWeight: 1,
      hitRadius: 42,
      speed: [0, 0],
      yRange: [0, 0],
      scale: 1,
      depth: 92,
      originX: 0.5,
      originY: 0.5,
      fixedPosition: { x: 2484, y: 218 },
      fixedFrameOffsets: [0, 9, 18, 27],
      excludedHitBoxes: [{ offsetX: 86, offsetY: 91, width: 20, height: 20 }],
      layerId: "overlay-lower",
      asset: {
        key: "windmill-chicken",
        url: `${graphics}/sprite_windmill_chicken_cycle_36frames.png`,
        frameWidth: 200,
        frameHeight: 200,
        frames: 36,
        frameRate: 18,
        repeat: -1
      },
      deathAsset: {
        key: "windmill-chicken-death",
        url: `${graphics}/sprite_windmill_chicken_death_72frames.png`,
        frameWidth: 200,
        frameHeight: 200,
        frames: 72,
        frameRate: 9,
        repeat: 0
      },
      hitSound: "wingHit"
    },
    {
      id: "leaf",
      label: "Leaf",
      behavior: "fall",
      points: 0,
      spawnWeight: 0,
      hitRadius: 24,
      speed: [52, 95],
      yRange: [-80, -40],
      scale: 0.7,
      depth: 112,
      asset: {
        key: "leaf",
        url: `${graphics}/sprite_leaf_19frames.png`,
        frameWidth: 90,
        frameHeight: 90,
        frames: 19,
        frameRate: 14,
        repeat: -1
      },
      hitAsset: {
        key: "leaf-hit",
        url: `${graphics}/sprite_leaf_hit_25frames.png`,
        frameWidth: 120,
        frameHeight: 120,
        frames: 25,
        frameRate: 20,
        repeat: 0
      },
      hitSound: "leafHit"
    }
  ],
  sounds: {
    ambient: { type: "file", url: `${sounds}/ambience_loop.ogg`, volume: 0.22, loop: true },
    music: { type: "file", url: `${sounds}/music_title.ogg`, volume: 0.1, loop: true },
    start: { type: "file", url: `${sounds}/sfx_game_start.ogg`, volume: 0.36 },
    gameOver: { type: "file", url: `${sounds}/sfx_game_over.ogg`, volume: 0.4 },
    shoot: { type: "file", url: `${sounds}/sfx_gun_blast.ogg`, volume: 0.42 },
    reload: { type: "file", url: `${sounds}/sfx_gun_reload.ogg`, volume: 0.38 },
    empty: { type: "file", url: `${sounds}/sfx_empty_magazine.ogg`, volume: 0.42 },
    chickenHit1: { type: "file", url: `${sounds}/sfx_chicken_hit_1.ogg`, volume: 0.46 },
    chickenHit2: { type: "file", url: `${sounds}/sfx_chicken_hit_2.ogg`, volume: 0.46 },
    chickenHit3: { type: "file", url: `${sounds}/sfx_chicken_hit_3.ogg`, volume: 0.46 },
    bigChickenPop: { type: "file", url: `${sounds}/sfx_big_chicken_pops_up.ogg`, volume: 0.42 },
    treeHit: { type: "file", url: `${sounds}/sfx_tree_hit.ogg`, volume: 0.5 },
    leafHit: { type: "file", url: `${sounds}/sfx_leaf_hit.ogg`, volume: 0.42 },
    wingHit: { type: "file", url: `${sounds}/sfx_wing_hit.ogg`, volume: 0.42 },
    scarecrowHit: { type: "file", url: `${sounds}/sfx_scarecrow_hit.ogg`, volume: 0.42 },
    shieldHit: { type: "file", url: `${sounds}/sfx_shield_hit.ogg`, volume: 0.42 },
    planeLoop: { type: "file", url: `${sounds}/sfx_airplane_loop.ogg`, volume: 0.18, loop: true },
    planeFail: { type: "file", url: `${sounds}/sfx_airplane_motor_failure.ogg`, volume: 0.42 },
    planeCrash: { type: "file", url: `${sounds}/sfx_airplane_crash.ogg`, volume: 0.48 },
    timeWarning: { type: "file", url: `${sounds}/sfx_time_running_warning.ogg`, volume: 0.34 },
    button: { type: "file", url: `${sounds}/ui_button_click.ogg`, volume: 0.35 },
    stamp: { type: "file", url: `${sounds}/ui_stamp.ogg`, volume: 0.34 }
  }
};
